import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ArrowRight, Loader2, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useUserRole } from "@/hooks/useUserRole";
import { toast } from "sonner";

export interface OtherTenantTrailerMatch {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  trailer_uuid: string;
  current_location_status: "at_site" | "in_transit" | "unknown";
  current_location_short_code: string | null;
  updated_at: string;
  has_active_leg: boolean;
}

interface TrailerCrossTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trailerIdText: string;
  matches: OtherTenantTrailerMatch[];
  /** Optional location to attach to the migrated trailer in the destination tenant. */
  destinationLocationId?: string | null;
  /** Status to set on the destination trailer after migration. Defaults to at_site if a location is provided, else unknown. */
  destinationStatus?: "at_site" | "in_transit" | "unknown";
  /** Called after a successful migration. Receives the destination trailer uuid and the source tenant info so callers can resume work. */
  onMigrated?: (destTrailerUuid: string, source: OtherTenantTrailerMatch) => void;
  /** Called when user opts to create a separate record in the active tenant. */
  onCreateSeparate?: (reason: string) => void;
}

export function TrailerCrossTenantDialog({
  open,
  onOpenChange,
  trailerIdText,
  matches,
  destinationLocationId,
  destinationStatus,
  onMigrated,
  onCreateSeparate,
}: TrailerCrossTenantDialogProps) {
  const { tenantId, activeTenant } = useTenant();
  const { roles } = useUserRole();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedSourceTenantId, setSelectedSourceTenantId] = useState<string | null>(
    matches[0]?.tenant_id ?? null,
  );

  const canMigrateHere =
    roles.includes("admin") ||
    roles.includes("dispatcher") ||
    roles.includes("terminal_staff");

  const reasonValid = reason.trim().length >= 5;

  const selectedMatch =
    matches.find((m) => m.tenant_id === selectedSourceTenantId) ?? matches[0];

  const finalStatus =
    destinationStatus ?? (destinationLocationId ? "at_site" : "unknown");

  const migrateMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId || !selectedMatch) throw new Error("Missing tenant context");
      const { data, error } = await supabase.rpc(
        "migrate_trailer_between_tenants" as any,
        {
          _trailer_id_text: trailerIdText,
          _from_tenant_id: selectedMatch.tenant_id,
          _to_tenant_id: tenantId,
          _reason: reason.trim(),
          _new_location_id: destinationLocationId ?? null,
          _new_status: finalStatus,
        },
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: (destUuid) => {
      toast.success(`Trailer ${trailerIdText} moved to ${activeTenant}`);
      queryClient.invalidateQueries({ queryKey: ["trailers-lookup"] });
      queryClient.invalidateQueries({ queryKey: ["trailers"] });
      onMigrated?.(destUuid, selectedMatch!);
      setReason("");
      onOpenChange(false);
    },
    onError: (err: any) => {
      const msg = err?.message ?? "Migration failed";
      if (msg.includes("TRAILER_HAS_ACTIVE_LEG")) {
        toast.error(
          "Cannot migrate — trailer is on an active load in the source tenant. Resolve it there first.",
        );
      } else if (msg.includes("Access denied")) {
        toast.error("You do not have permission to move trailers into this tenant.");
      } else {
        toast.error(msg);
      }
    },
  });

  const handleCreateSeparate = async () => {
    if (!reasonValid) {
      toast.error("A reason of at least 5 characters is required.");
      return;
    }
    try {
      await supabase.rpc("log_audit_event" as any, {
        _action: "trailer_duplicate_acknowledged",
        _resource_type: "trailers",
        _resource_id: trailerIdText,
        _details: {
          trailer_id: trailerIdText,
          destination_tenant: activeTenant,
          known_in_tenants: matches.map((m) => m.tenant_slug),
          reason: reason.trim(),
        } as any,
      });
    } catch {
      // best-effort audit; do not block UX
    }
    onCreateSeparate?.(reason.trim());
    setReason("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Trailer exists in another environment
          </DialogTitle>
          <DialogDescription>
            Trailer{" "}
            <span className="font-mono font-semibold text-foreground">
              {trailerIdText}
            </span>{" "}
            already has a record in {matches.length === 1 ? "another" : `${matches.length} other`}{" "}
            tenant. Move it here to keep a single source of truth, or create a separate record (not
            recommended).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Existing record{matches.length > 1 ? "s" : ""}
          </Label>
          <div className="space-y-2">
            {matches.map((m) => {
              const selected = selectedMatch?.tenant_id === m.tenant_id;
              return (
                <button
                  key={m.tenant_id}
                  type="button"
                  onClick={() => setSelectedSourceTenantId(m.tenant_id)}
                  className={`w-full rounded-md border p-3 text-left transition-colors ${
                    selected ? "border-primary bg-accent/40" : "hover:bg-accent/20"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{m.tenant_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {m.tenant_slug}
                        </Badge>
                        {m.has_active_leg && (
                          <Badge variant="destructive" className="text-xs">
                            Active load
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {m.current_location_short_code ?? "Unknown"}
                        </span>
                        <span>· {m.current_location_status}</span>
                        <span>
                          ·{" "}
                          {formatDistanceToNow(new Date(m.updated_at), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedMatch?.has_active_leg && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This trailer is on an active load in{" "}
                <strong>{selectedMatch.tenant_name}</strong>. Coordinate with that
                tenant's dispatch and complete or cancel the load before moving it.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="trailer-migration-reason" className="text-xs">
              Reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="trailer-migration-reason"
              placeholder="Why is this trailer moving environments? (min 5 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>

          {!showAdvanced ? (
            <button
              type="button"
              onClick={() => setShowAdvanced(true)}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              Show advanced options
            </button>
          ) : (
            <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
              <p className="mb-2 font-medium text-foreground">Create a separate record</p>
              <p className="mb-2">
                A duplicate trailer record will be created in{" "}
                <strong className="text-foreground">{activeTenant}</strong>. This causes data drift
                and is logged for review. Use only if these are physically distinct trailers that
                happen to share the same identifier.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCreateSeparate}
                disabled={!reasonValid || migrateMutation.isPending}
              >
                Create separate record here
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={migrateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => migrateMutation.mutate()}
            disabled={
              !canMigrateHere ||
              !reasonValid ||
              !selectedMatch ||
              selectedMatch.has_active_leg ||
              migrateMutation.isPending
            }
            title={
              !canMigrateHere
                ? "You need an admin, dispatcher, or terminal staff role here to migrate trailers."
                : selectedMatch?.has_active_leg
                  ? `Cannot migrate — trailer is on an active load in ${selectedMatch.tenant_name}.`
                  : undefined
            }
          >
            {migrateMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="mr-2 h-4 w-4" />
            )}
            Move trailer to {activeTenant}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Helper: check whether a trailer string exists in any other tenant. Returns [] when none. */
export async function findTrailerInOtherTenants(
  trailerIdText: string,
  excludeTenantId: string,
): Promise<OtherTenantTrailerMatch[]> {
  const { data, error } = await supabase.rpc(
    "find_trailer_in_other_tenants" as any,
    {
      _trailer_id: trailerIdText.trim(),
      _exclude_tenant_id: excludeTenantId,
    },
  );
  if (error) {
    console.error("findTrailerInOtherTenants failed", error);
    return [];
  }
  return (data as OtherTenantTrailerMatch[] | null) ?? [];
}